import React from 'react';
import { Box, LinearProgress, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BalanceBucketsDto, FinanceTotalsDto, FinanceTrendPointDto } from '../../../../common/interfaces/invoicing.interface';
import { FINANCE_DEBTS } from '../../../../common/constants/routes.constants';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { forMonth, inMonth, monthKeyOf, monthName } from './monthText';
import { groupQuery, KINDERGARTEN_SCOPE, OverviewScope } from './scope';

const cardSx = {
  border: 1,
  borderColor: 'line',
  borderRadius: 4,
  p: '18px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  minWidth: 0
} as const;
const percent = (value: number) => new Intl.NumberFormat('sr-RS', { maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(value);

/** "↑ 2,1 % u odnosu na jul"; nothing for a month with nothing before it to compare to. */
export const Comparison: React.FC<{ now: number; before: number | null; beforeKey: string | null }> = ({ now, before, beforeKey }) => {
  const { t } = useTranslation();
  if (before === null || !beforeKey || before === 0) return null;
  const change = ((now - before) / before) * 100;
  const down = change < 0;
  return (
    <Typography sx={{ fontSize: 12, color: down ? 'warning.main' : 'text.secondary' }}>
      {t('finance.figures2.comparison', { arrow: down ? '↓' : '↑', value: percent(Math.abs(change)), month: monthName(beforeKey) })}
    </Typography>
  );
};

/**
 * The month in four figures: what was issued for it, how much of that is in, what came in
 * during it (for any month), and what is owed today. The last one is today's, from the
 * debts page, and the whole card leads there.
 */
const MonthFigures: React.FC<{
  month: string;
  totals: FinanceTotalsDto;
  /** The trend's points, for the month before; absent before it has loaded. */
  points: FinanceTrendPointDto[];
  buckets: BalanceBucketsDto | null;
  kindergartenId: string;
  scope?: OverviewScope;
}> = ({ month, totals, points, buckets, kindergartenId, scope = KINDERGARTEN_SCOPE }) => {
  const { t } = useTranslation();
  const index = points.findIndex(point => monthKeyOf(point.year, point.month) === month);
  const previous = index > 0 ? points[index - 1] : null;
  const previousKey = previous ? monthKeyOf(previous.year, previous.month) : null;
  const share = totals.chargedInMonth > 0 ? (totals.collectedOfIssued / totals.chargedInMonth) * 100 : null;
  const owed = buckets?.all ?? { amount: 0, count: 0 };
  const late = (buckets?.late30.amount ?? 0) + (buckets?.late.amount ?? 0);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
      <Box component="section" aria-label={t('finance.figures2.issued', { month: forMonth(t, month) })} sx={cardSx}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t('finance.figures2.issued', { month: forMonth(t, month) })}
        </Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(totals.chargedInMonth)}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t('finance.figures2.invoices', { count: totals.issuedCount })}
          {totals.draftCount > 0 && ` · ${t('finance.figures2.drafts', { count: totals.draftCount })}`}
        </Typography>
        <Comparison now={totals.chargedInMonth} before={previous?.chargedInMonth ?? null} beforeKey={previousKey} />
      </Box>

      <Box component="section" aria-label={t('finance.figures2.collected')} sx={cardSx}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('finance.figures2.collected')}</Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {share === null ? '—' : `${percent(share)} %`}
        </Typography>
        {share !== null && (
          <>
            <LinearProgress
              variant="determinate"
              value={Math.min(share, 100)}
              aria-label={t('finance.figures2.collected')}
              sx={{
                height: 6,
                borderRadius: 999,
                bgcolor: 'line',
                '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 999 }
              }}
            />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('finance.figures2.collectedOf', {
                paid: formatMoney(totals.collectedOfIssued),
                issued: formatMoney(totals.chargedInMonth)
              })}
            </Typography>
          </>
        )}
      </Box>

      <Box component="section" aria-label={t('finance.figures2.received', { month: inMonth(t, month) })} sx={cardSx}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t('finance.figures2.received', { month: inMonth(t, month) })}
        </Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(totals.paidInMonth)}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t('finance.figures2.payments', { count: totals.paymentCount })}
        </Typography>
        <Comparison now={totals.paidInMonth} before={previous?.paidInMonth ?? null} beforeKey={previousKey} />
      </Box>

      <Link
        component={RouterLink}
        to={`${FINANCE_DEBTS}?kindergartenId=${kindergartenId}${groupQuery(scope)}`}
        underline="none"
        aria-label={t('finance.figures2.owedToday')}
        sx={{ ...cardSx, color: 'inherit', '&:hover': { borderColor: 'field.hover' } }}
      >
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('finance.figures2.owedToday')}</Typography>
        <Typography
          sx={{ fontSize: 26, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: owed.amount > 0 ? 'error.main' : 'text.primary' }}
        >
          {formatMoney(owed.amount)}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t('balances.cards.children', { count: owed.count })}
          {late > 0 && ` · ${t('finance.figures2.ofWhichLate', { amount: formatMoney(late) })}`} →
        </Typography>
      </Link>
    </Box>
  );
};

export default MonthFigures;
